/**
 * Backend Unavailable Recovery Modal (Deliverable C)
 * 
 * Shows when backend returns 520/5xx or network failure.
 * Does NOT clear auth token. Preserves job context.
 * Allows retry or navigation to dashboard.
 */

import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

export const BackendUnavailableModal = ({ 
  isOpen, 
  onRetry, 
  onBackToDashboard,
  error,
  context 
}) => {
  if (!isOpen) return null;

  const classification = error?.classification || 'UNKNOWN';
  const status = error?.status || 0;
  const jobId = context?.jobId;

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent className="bg-slate-900 border-orange-500/50 text-slate-50 max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-orange-400">
            <AlertTriangle className="w-5 h-5" />
            Backend Temporarily Unavailable
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Status indicator */}
          <div className="bg-orange-950/30 border border-orange-600/30 rounded-lg p-4">
            <p className="text-sm text-orange-200 font-medium">
              {status === 520 ? 'Gateway Error (520)' : 
               status === 0 ? 'Network Connection Lost' :
               `Server Error (${status})`}
            </p>
            <p className="text-xs text-orange-300/70 mt-2">
              {classification === 'GATEWAY_520' 
                ? 'The request did not reach the server. This is typically a proxy or edge issue.'
                : classification === 'NETWORK_FAILURE'
                ? 'Unable to connect to the server. Check your internet connection.'
                : 'The server encountered an error processing your request.'}
            </p>
          </div>

          {/* Reassurance */}
          <div className="text-sm text-slate-300">
            <p className="font-medium text-green-400">✓ Your session is preserved</p>
            <p className="text-xs text-slate-400 mt-1">
              You are still logged in. No data has been lost.
            </p>
            {jobId && (
              <p className="text-xs text-slate-400 mt-1">
                Job ID: <span className="font-mono text-slate-300">{jobId}</span>
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <Button
              onClick={onRetry}
              className="flex-1 bg-orange-600 hover:bg-orange-500"
              data-testid="backend-unavailable-retry-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Retry
            </Button>
            
            <Button
              variant="outline"
              onClick={onBackToDashboard}
              className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800"
              data-testid="backend-unavailable-dashboard-btn"
            >
              <Home className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </div>

          {/* Technical details (collapsed) */}
          <details className="text-xs text-slate-500">
            <summary className="cursor-pointer hover:text-slate-400">Technical Details</summary>
            <pre className="mt-2 p-2 bg-slate-950 rounded text-xs overflow-auto max-h-24">
              {JSON.stringify({
                status,
                classification,
                timestamp: new Date().toISOString(),
                jobId: jobId || 'none'
              }, null, 2)}
            </pre>
          </details>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BackendUnavailableModal;
